import React, { memo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import history from 'utils/history';
import useDeviceDetect from 'utils/useDeviceDetect';

// styles
import './style.scss';

// antd component
import { Button, Modal } from 'antd';

//assets
import {
  ArrowLeftOutlined,
  CloseOutlined,
  MoreOutlined,
  PlusOutlined,
} from '@ant-design/icons';

// global user
import { useGetUserChildren, useGetLocalUser, useGetLocalActiveChild } from 'engine/Contexts/User.context';

// components
import StepByStepWrapper from 'mobile/components/StepByStepWrapper';
// cta notifications
import FinalScreen from 'mobile/components/FinalScreen';
import NotificationForParents from 'mobile/components/NotificationForParents';
// forms
import AbsenceTypeForm from 'mobile/components/Forms/AbsenceTypeForm';
import SicknessForm from 'mobile/components/Forms/SicknessForm';
// medical/no-medical
import ReasonForm from 'mobile/components/Forms/ReasonForm';
import DurationForm from 'mobile/components/Forms/DurationForm';
import MediaForm from 'mobile/components/Forms/MediaForm';
import MediaFormPreview from 'mobile/components/Forms/MediaFormPreview';
import ParentsUndertakeForm from 'mobile/components/Forms/ParentsUndertakeForm';
import ResumeForm from 'mobile/components/Forms/ResumeForm';

// utils
import { clean } from 'utils/helpers/helper.general';
// hooks
import { useRefState } from 'utils/hooks';

function CreateAbsence({
  absence,
  buttonText,
  collapseButton,
  hideButton,
  isOpen,
  // actions
  setOpen,
  change,
  // default
  className,
}) {
  const childClassName = classNames('create-absence-wrapper', className);
  const { isMobile } = useDeviceDetect();

  const isEdit = absence !== null && absence?._id && absence;
  const localUser = useGetLocalUser()();
  const localActiveChild = useGetLocalActiveChild()();
  const children = useGetUserChildren()();

  const [step, setStep] = useState(isEdit ? 4 : 1);
  const [totalStep, setTotalStep] = useState(4);
  const [showFinal, setShowFinal] = useState(false);

  let initAbsence = {
    status: 'pending',
    created_by: localUser?._id || null,
    type: 'medical',
  };
  let initStdInfo = null;

  if (localUser?.role == 'parent') {
    initAbsence.student = localActiveChild?._id;
    initAbsence.parent = localUser?._id;
    initStdInfo = children?.find(s => s?._id == localActiveChild?._id);
  }

  const [absenceInfo, absenceInfoRef, setAbsenceInfo] = useRefState(
    isEdit || initAbsence,
  );
  const [absenceType, setAbsenceType] = useState(absence?.type || false);
  const [selectedStdObj, setSelectedStdObj] = useState(initStdInfo);

  // default form options
  let formOptions = {
    formData: absenceInfoRef?.current || initAbsence,
    selectedStudent: selectedStdObj,
    setSelectedStudent: obj => setSelectedStdObj(obj),
    isEdit: isEdit,
    currentStep: step,
    // Step Navigation
    nextStep: () => step != totalStep && setStep(step + 1),
    prevStep: () => {
      return step > 1 && setStep(step - 1);
    },
    goToStep: number => setStep(number),
    result: values => resultData(values),
  };

  // set Absence type from history states
  useEffect(() => {
    const locationStateOpen = history?.location?.state?.openAbsence;
    if (locationStateOpen) {
      setAbsenceType(locationStateOpen);
      setOpen(true);
      const dataAbs = absence || initAbsence;
      setAbsenceInfo({ ...dataAbs, type: locationStateOpen });
      setSelectedStdObj(absence?.student || null);
    }
    if (localUser?.role == 'parent') {
      setSelectedStdObj(initStdInfo);
    }
  }, []);

  // set Absence type from history states
  useEffect(() => {
    if (localUser?.role == 'parent') {
      setSelectedStdObj(initStdInfo);
    }
    if (!!isOpen) {
      setStep(1);
      setShowFinal(false);
    }
  }, [isOpen]);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (absence !== null && absence) {
      formOptions.formData = absence;
      setAbsenceInfo(absence);
      setAbsenceType(absence?.type);
      if (localUser?.role == 'parent') {
        setSelectedStdObj(initStdInfo || absence?.student || null);
      } else {
        setSelectedStdObj(absence?.student || null);
      }
    }
  }, [absence]);

  /**
   * Change total amount Step by Absence Type
   */
  useEffect(() => {
    if (absenceType) {
      switch (absenceType) {
        case 'medical':
          if (isEdit && localUser?.role == 'parent') {
            setTotalStep(3);
            setStep(3);
          } else {
            setTotalStep(7);
            isEdit && setStep(7);
          }
          break;
        case 'no-medical':
          if (isEdit && localUser?.role == 'parent') {
            setTotalStep(3);
            setStep(3);
          } else {
            setTotalStep(7);
            isEdit && setStep(7);
          }
          break;
        case 'sickness':
          if (isEdit && localUser?.role == 'parent') {
            setTotalStep(1);
          } else {
            setTotalStep(2);
            isEdit && setStep(2);
          }
          break;
      }
    }
  }, [absenceType, isEdit]);

  // Main result/save data
  const resultData = result => {
    let absenceCurrent = absenceInfo;

    if (result.hasOwnProperty('type')) {
      setAbsenceType(result.type);
    }

    const newObj = { ...absenceCurrent, ...result };

    if (newObj?.type == 'sickness') {
      newObj.status = 'sick';
    }

    if (newObj?.type == 'sickness' && result.hasOwnProperty('status')) {
      newObj.status = result.status;
    }

    setAbsenceInfo(newObj);

    if (localUser?._id && step == totalStep) {
      // console.debug('Last Step!!! Submit!');
      setShowFinal(true);
      change(clean(newObj));
    }
  };

  // Update Absence status, only for 'sickness' and 'parent' user role
  const resultSicknessCallback = status => {
    let absenceCurrent = absenceInfo;

    if (absenceCurrent?.status != status) {
      const newObj = {
        ...absenceCurrent,
        status: status,
      };
      setAbsenceInfo(newObj);

      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
        change(clean(newObj));
      }
    } else {
      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
      }
    }

    closeModal();
  };

  const closeModal = () => {
    setShowFinal(false);
    setOpen(false);
    // ----
    setAbsenceInfo(initAbsence);
    setStep(1);
    setAbsenceType(false);
    setSelectedStdObj(null);
  };
  const openModal = () => {
    setStep(1);
    setShowFinal(false);
    setOpen(true);
  };

  const studentName = useCallback((stdObj, crop) => {
    const stdNameStr = stdObj?.description
      ? `${stdObj?.description?.lastname} ${
          crop && stdObj?.description?.firstname
            ? stdObj?.description?.firstname.substring(0, 1) + '.'
            : stdObj?.description?.firstname
        }`
      : '';
    return (
      stdNameStr && <span className="student-name-title">{stdNameStr}</span>
    );
  }, []);

  const finalNotification =
    absenceType == 'sickness' ? (
      <FinalScreen
        title="L’absence maladie a été transmise"
        continueAction={closeModal}
      >
        {`L’information a été transmise à l’établissement scolaire de
      l’étudiant(e). <br /> <br /> En cas d’absence prolongée, vous receverez une notification
      afin de confirmer que l’élève est toujours malade, dès demain.`}
      </FinalScreen>
    ) : (
      <>
        {isEdit && localUser?.role == 'teacher' ? (
          <FinalScreen
            title="L’absence a été modifiée"
            content="L’information a été transmise aux parents de l’étudiant."
            continueAction={closeModal}
          />
        ) : (
          <FinalScreen continueAction={closeModal} />
        )}
      </>
    );

  const medicalSteps = stepNumber => {
    switch (stepNumber) {
      case 1:
        return (
          <StepByStepWrapper
            number={1}
            isActive={step == 1}
            header={`Étape ${1} sur ${totalStep}`}
            title="Motif de l’absence"
          >
            <AbsenceTypeForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 2:
        return (
          <StepByStepWrapper
            number={2}
            isActive={step == 2}
            header={`Étape ${1} sur ${4}`}
            title="Motif de l’absence"
          >
            <ReasonForm {...formOptions} type={absenceType} />
          </StepByStepWrapper>
        );
      case 3:
        return (
          <StepByStepWrapper
            number={3}
            isActive={step == 3}
            header={`Étape ${2} sur ${4}`}
            title="Durée de l’absence"
          >
            <DurationForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 4:
        return (
          <StepByStepWrapper
            number={4}
            isActive={step == 4}
            header={`Étape ${3} sur ${4}`}
            title="Justificatif de l’absence"
          >
            <MediaForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 5:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={5}
                isActive={step == 5}
                header={`Étape ${3} sur ${4}`}
                title="Justificatif de l’absence"
              >
                <MediaFormPreview {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 6:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={6}
                isActive={step == 6}
                header={`Étape ${4} sur ${4}`}
                title="Les parents s'engagent à"
              >
                <ParentsUndertakeForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 7:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={7}
                isActive={step == 7}
                header={
                  selectedStdObj ? `Récapitulatif de la demande` : 'Résumé'
                }
                title={
                  studentName(selectedStdObj) || `Récapitulatif de la demande`
                }
              >
                <ResumeForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
    }
  };

  const medicalStepsParentEdit = stepNumber => {
    switch (stepNumber) {
      case 1:
        return (
          <StepByStepWrapper
            number={1}
            isActive={step == 1}
            header={`Étape ${3} sur ${4}`}
            title="Justificatif de l’absence"
          >
            <MediaForm {...formOptions} />
          </StepByStepWrapper>
        );
      case 2:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={2}
                isActive={step == 2}
                header={`Étape ${3} sur ${4}`}
                title="Justificatif de l’absence"
              >
                <MediaFormPreview {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
      case 3:
        return (
          <>
            {!showFinal && (
              <StepByStepWrapper
                number={3}
                isActive={step == 3}
                header={
                  selectedStdObj ? `Récapitulatif de la demande` : 'Résumé'
                }
                title={
                  studentName(selectedStdObj) || `Récapitulatif de la demande`
                }
              >
                <ResumeForm {...formOptions} />
              </StepByStepWrapper>
            )}
            {/* // Last Step Notificationswindows */}
            {showFinal && finalNotification}
          </>
        );
    }
  };

  return (
    <div className={childClassName}>
      {!hideButton && (
        <Button className={`create-absence-wrapper__modal-btn ${collapseButton ? 'collapsed-btn' : ''}`} onClick={openModal}>
          <PlusOutlined />{!!buttonText && !collapseButton ? (<span className='text'>{buttonText}</span>) : ''}
        </Button>
      )}
      {isEdit && localUser?.role == 'parent' && absenceType == 'sickness' ? (
        <NotificationForParents
          visibility={isOpen}
          okHandle={() => resultSicknessCallback('sickness-finish')}
          closeHandle={() => resultSicknessCallback('sick')}
          cancelHandle={() => resultSicknessCallback('sick')}
          child={selectedStdObj}
        />
      ) : (
        <>
          {/* Create Absence Modal */}
          <Modal
            className="create-absence"
            title={
              <>
                {isMobile && (
                  <>
                    {/* <MoreOutlined className="create-absence__mobile-menu" /> */}
                    <div className="create-absence__mobile-menu" />
                  </>
                )}
                <div className="modal-title">
                  {'Demande d’absence'}
                  {selectedStdObj && ': '}
                  {studentName(selectedStdObj, true)}
                </div>
              </>
            }
            open={isOpen}
            closeIcon={
              <span className="create-absence__close" onClick={closeModal}>
                {!isMobile ? (
                  <>
                    <CloseOutlined /> Close
                  </>
                ) : (
                  <ArrowLeftOutlined />
                )}
              </span>
            }
            onCancel={closeModal}
            footer={[]}
          >
            {isEdit && localUser?.role == 'parent' ? (
              <>{medicalStepsParentEdit(step)}</>
            ) : (
              <>
                {absenceType && absenceType == 'sickness' ? (
                  <>
                    {!showFinal && (
                      <>
                        <StepByStepWrapper
                          number={1}
                          isActive={step == 1}
                          header={`Étape ${1} sur ${totalStep}`}
                          title="Motif de l’absence"
                        >
                          <AbsenceTypeForm {...formOptions} />
                        </StepByStepWrapper>
                        <StepByStepWrapper
                          number={2}
                          isActive={step == 2}
                          header={`Demande d’absence`}
                          title="Durée de l’absence maladie"
                        >
                          <SicknessForm {...formOptions} />
                        </StepByStepWrapper>
                      </>
                    )}
                    {showFinal && finalNotification}
                  </>
                ) : (
                  medicalSteps(step)
                )}
              </>
            )}
          </Modal>
        </>
      )}
    </div>
  );
}

CreateAbsence.defaultProps = {
  isOpen: false,
  buttonText: 'Nouvelle absence',
  collapseButton: false,
  hideButton: false
};

CreateAbsence.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  absence: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.bool,
  ]),
  change: PropTypes.func.isRequired,
};

export default memo(CreateAbsence);